import { useEffect, useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import { useChat } from "@/features/chat"
import { OnboardingLockWaiting, useOnboardingLock } from "@/features/onboarding-lock"
import { useUserZodiac } from "@/features/webapp"
import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { useQuizStore } from "@/stores/quiz-store"

import { LoadingSpinner } from "@/shared/components/loading-spinner"
import { usePalmContent } from "../hooks/use-palm-content"
import { getStoredPalmReading } from "../lib/palm-reading"
import { PalmResult } from "./palm-result"
import { ReportQuiz } from "./report-quiz"

/** Palmistry tab (`/webapp/palmistry`): the report once unlocked, else the scan flow. */
export function PalmistryPage() {
  const navigate = useNavigate()
  const content = usePalmContent()
  const { isPalmReadingLocked, setPalmReportModalOpen } = useChat()
  const { isLocked, remainingLabel } = useOnboardingLock()
  const zodiac = useUserZodiac()
  const name = useQuizStore((s) => s.answers.name)

  // Read the stored reading once (stable across renders). After a mid-session
  // scan, this stays `null` so ReportQuiz can keep showing "being prepared".
  const [reading] = useState(getStoredPalmReading)

  // Fresh entry while the 10‑min lock is on (e.g. Personalized Reports CTA):
  // don't remount the hand-scan intro — open the same countdown sheet as the tab.
  useEffect(() => {
    if (!isPalmReadingLocked || !reading) return
    setPalmReportModalOpen(true)
    void navigate({ to: WebappRoutes.ADVISORS })
  }, [isPalmReadingLocked, reading, setPalmReportModalOpen, navigate])

  if (isLocked("palmistry_tab")) {
    return <OnboardingLockWaiting remainingLabel={remainingLabel} />
  }

  if (isPalmReadingLocked && reading) {
    return null
  }

  if (reading && !isPalmReadingLocked) {
    if (!content)
      return (
        <div className="flex min-h-[60dvh] items-center justify-center">
          <LoadingSpinner />
        </div>
      )
    return (
      <div className="-mx-4">
        <PalmResult
          content={content}
          reading={reading}
          name={typeof name === "string" ? name : "User"}
          zodiac={zodiac}
        />
      </div>
    )
  }

  return <ReportQuiz />
}
